import { TourStep } from '@/types/tour';

/**
 * Utility function to wait for DOM elements to be available
 * @param selectors - Array of CSS selectors to check for existence
 * @param timeout - Maximum time to wait in milliseconds (default: 10000ms)
 * @param interval - Interval between checks in milliseconds (default: 100ms)
 * @returns Promise that resolves when all elements exist or rejects on timeout
 */
export const waitForElements = (
  selectors: string[], 
  timeout = 10000, 
  interval = 100
): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    // Track start time to enforce timeout
    const startTime = Date.now();
    
    const checkElements = () => {
      // Check if all selectors exist in the DOM
      const allExist = selectors.every(selector => document.querySelector(selector));
      
      if (allExist) {
        // All elements found, resolve the promise
        resolve(true);
      } else if (Date.now() - startTime > timeout) {
        // Timeout reached, reject the promise
        console.warn(`Timeout waiting for elements: ${selectors.join(', ')}`);
        reject(new Error(`Timeout waiting for elements: ${selectors.join(', ')}`));
      } else {
        // Continue checking after the specified interval
        setTimeout(checkElements, interval);
      }
    };
    
    // Start checking
    checkElements();
  });
};

/**
 * Extract all element selectors from tour steps
 * @param steps - Array of tour steps
 * @returns Array of unique element selectors
 */
export const extractTourSelectors = (steps: TourStep[]): string[] => {
  const selectors = steps
    .filter(step => step.attachTo && typeof step.attachTo.element === 'string')
    .map(step => step.attachTo?.element as string);
  
  // Return unique selectors
  return [...new Set(selectors)];
};
